import Phaser from 'phaser';
import { gameState } from '../services/gameState.js';
import { sound } from '../services/audioService.js';
import { ensureTextures } from './textureGenerator.js';

export class MainScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MainScene' });
    this.character = null;
    this.currentMoodId = 'happy';
    this.stateUnsubscribe = null;
    this.particles = null;
    this.isPetting = false;
  }

  create() {
    ensureTextures(this);

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Room / Stage background glow
    this.bgGlow = this.add.circle(width / 2, height / 2 + 10, 80, 0xFFFFFF, 0.05);

    // Create Character Container
    this.charContainer = this.add.container(width / 2, height / 2 + 15);

    // Character Shadow
    this.shadow = this.add.ellipse(0, 52, 90, 22, 0x000000, 0.25);
    this.charContainer.add(this.shadow);

    // Character Body Graphics
    this.bodyGraphics = this.add.graphics();
    this.charContainer.add(this.bodyGraphics);

    // Character Face Graphics
    this.faceGraphics = this.add.graphics();
    this.charContainer.add(this.faceGraphics);

    // Interactive Petting Hit Area
    const hitArea = this.add.circle(0, 0, 55, 0x000000, 0.001);
    hitArea.setInteractive({ cursor: 'pointer' });
    this.charContainer.add(hitArea);

    hitArea.on('pointerdown', () => {
      this.petCharacter();
    });

    // Particle Emitter for mood effects
    this.setupParticleEmitters();

    // Subscribe to game state changes
    this.stateUnsubscribe = gameState.subscribe((state) => {
      this.onMoodChanged(state.mood);
    });
  }

  setupParticleEmitters() {
    // Emitters for different moods
    this.sparkleEmitter = this.add.particles(this.cameras.main.width / 2, this.cameras.main.height / 2, 'particle_star', {
      speed: { min: 40, max: 120 },
      scale: { start: 0.35, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: 1200,
      frequency: 350,
      tint: [0xFFD700, 0xFFFFFF, 0xFF69B4]
    });

    this.heartEmitter = this.add.particles(this.cameras.main.width / 2, this.cameras.main.height / 2, 'particle_heart', {
      speed: { min: 50, max: 140 },
      scale: { start: 0.45, end: 0 },
      lifespan: 1000,
      emitting: false
    });

    this.tearEmitter = this.add.particles(this.cameras.main.width / 2, this.cameras.main.height / 2 + 10, 'particle_tear', {
      speedY: { min: 40, max: 90 },
      speedX: { min: -15, max: 15 },
      scale: { start: 0.4, end: 0.1 },
      lifespan: 1000,
      frequency: 500,
      emitting: false
    });

    this.fireEmitter = this.add.particles(this.cameras.main.width / 2, this.cameras.main.height / 2 - 20, 'particle_fire', {
      speedY: { min: -50, max: -110 },
      speedX: { min: -30, max: 30 },
      scale: { start: 0.5, end: 0 },
      lifespan: 800,
      frequency: 250,
      emitting: false
    });
  }

  onMoodChanged(mood) {
    if (this.currentMoodId === mood.id && this.activeTween) return;
    this.currentMoodId = mood.id;

    // Sound cue
    sound.playMoodCue(mood.id);

    // Stop current animations
    if (this.activeTween) {
      this.activeTween.stop();
    }
    if (this.spinTween) {
      this.spinTween.stop();
    }

    // Configure Particles
    this.sparkleEmitter.stop();
    this.tearEmitter.stop();
    this.fireEmitter.stop();

    if (mood.id === 'happy') {
      this.sparkleEmitter.start();
      this.startHappyAnimation();
    } else if (mood.id === 'serene') {
      this.startSereneAnimation();
    } else if (mood.id === 'sad') {
      this.tearEmitter.start();
      this.startSadAnimation();
    } else if (mood.id === 'mad') {
      this.fireEmitter.start();
      this.startMadAnimation();
    }

    this.drawCharacter(mood.id);
  }

  drawCharacter(moodId, isBlinking = false) {
    const body = this.bodyGraphics;
    const face = this.faceGraphics;
    body.clear();
    face.clear();

    // Body Color based on Mood
    let primaryColor = 0x6C5CE7;
    let secondaryColor = 0xA29BFE;

    if (moodId === 'happy') {
      primaryColor = 0xFFBE0B; // Vibrant energetic gold
      secondaryColor = 0xFFD166;
    } else if (moodId === 'serene') {
      primaryColor = 0x06D6A0; // Calm mint/cyan
      secondaryColor = 0x70E000;
    } else if (moodId === 'sad') {
      primaryColor = 0x3A86FF; // Melancholy blue
      secondaryColor = 0x60A5FA;
    } else if (moodId === 'mad') {
      primaryColor = 0xEF476F; // Fiery Crimson
      secondaryColor = 0xFF5252;
    }

    // 1. Draw Cute Blob Body
    body.fillStyle(primaryColor, 1);
    body.fillRoundedRect(-42, -45, 84, 86, 38);

    // Body highlight
    body.fillStyle(0xFFFFFF, 0.25);
    body.fillCircle(-16, -26, 12);

    // 2. Cheeks Blush
    if (moodId !== 'mad') {
      face.fillStyle(0xFF4081, 0.45);
      face.fillCircle(-24, 6, 7);
      face.fillCircle(24, 6, 7);
    } else {
      // Angry red cheeks
      face.fillStyle(0xFF0000, 0.6);
      face.fillCircle(-24, 6, 8);
      face.fillCircle(24, 6, 8);
    }

    // 3. Eyes & Expressions
    if (moodId === 'happy') {
      // Happy curved arcs ^ ^
      face.lineStyle(3.5, 0x1A1B2F, 1);
      face.beginPath();
      face.arc(-16, -6, 8, Math.PI * 1.1, Math.PI * 1.9, false);
      face.stroke();

      face.beginPath();
      face.arc(16, -6, 8, Math.PI * 1.1, Math.PI * 1.9, false);
      face.stroke();

      // Big Open Happy Mouth D
      face.fillStyle(0x1A1B2F, 1);
      face.beginPath();
      face.arc(0, 10, 10, 0, Math.PI, false);
      face.fill();
      // Tongue
      face.fillStyle(0xFF6B81, 1);
      face.fillCircle(0, 16, 5);

    } else if (moodId === 'serene') {
      // Peaceful blinking or relaxed oval eyes
      face.fillStyle(0x1A1B2F, 1);
      face.fillCircle(-16, -4, 5);
      face.fillCircle(16, -4, 5);
      // Sparkle catchlight
      face.fillStyle(0xFFFFFF, 1);
      face.fillCircle(-18, -6, 2);
      face.fillCircle(14, -6, 2);

      // Sweet subtle smile :)
      face.lineStyle(2.5, 0x1A1B2F, 1);
      face.beginPath();
      face.arc(0, 8, 7, Math.PI * 0.1, Math.PI * 0.9, false);
      face.stroke();

    } else if (moodId === 'sad') {
      // Drooping sad eyes
      face.fillStyle(0x1A1B2F, 1);
      face.fillCircle(-16, -2, 6);
      face.fillCircle(16, -2, 6);
      // Big watery shine
      face.fillStyle(0x80D8FF, 0.9);
      face.fillCircle(-14, 0, 3);
      face.fillCircle(18, 0, 3);

      // Downward Frown :(
      face.lineStyle(3, 0x1A1B2F, 1);
      face.beginPath();
      face.arc(0, 16, 8, Math.PI * 1.1, Math.PI * 1.9, false);
      face.stroke();

    } else if (moodId === 'mad') {
      // Sharp slanted angry eyebrows \ /
      face.lineStyle(4, 0x1A1B2F, 1);
      face.beginPath();
      face.moveTo(-26, -14);
      face.lineTo(-8, -6);
      face.moveTo(26, -14);
      face.lineTo(8, -6);
      face.stroke();

      // Narrow glare eyes
      face.fillStyle(0x1A1B2F, 1);
      face.fillCircle(-16, -2, 5);
      face.fillCircle(16, -2, 5);

      // Grumpy jagged or zigzag mouth
      face.lineStyle(3, 0x1A1B2F, 1);
      face.beginPath();
      face.moveTo(-12, 14);
      face.lineTo(-4, 10);
      face.lineTo(4, 15);
      face.lineTo(12, 11);
      face.stroke();
    }
  }

  startHappyAnimation() {
    // Happy jumping about & bouncing
    this.activeTween = this.tweens.add({
      targets: this.charContainer,
      y: this.cameras.main.height / 2 - 35,
      scaleY: 1.12,
      scaleX: 0.9,
      duration: 380,
      yoyo: true,
      repeat: -1,
      ease: 'Quad.easeInOut',
      onYoyo: () => {
        // Squash on floor hit
        this.tweens.add({
          targets: this.shadow,
          scaleX: 1.25,
          scaleY: 1.25,
          alpha: 0.35,
          duration: 180,
          yoyo: true
        });
      }
    });
  }

  startSereneAnimation() {
    // Gentle hovering & steady calm breathing
    this.charContainer.y = this.cameras.main.height / 2 + 15;
    this.activeTween = this.tweens.add({
      targets: this.charContainer,
      y: this.cameras.main.height / 2 + 8,
      scaleY: 1.04,
      scaleX: 0.97,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  startSadAnimation() {
    // Slumped low posture, trembling slightly
    this.charContainer.y = this.cameras.main.height / 2 + 28;
    this.activeTween = this.tweens.add({
      targets: this.charContainer,
      scaleY: 0.88,
      scaleX: 1.08,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  startMadAnimation() {
    // Heavy stomping & angry shaking
    this.charContainer.y = this.cameras.main.height / 2 + 15;
    this.activeTween = this.tweens.add({
      targets: this.charContainer,
      x: '+=3',
      y: '-=8',
      scaleY: 1.05,
      duration: 120,
      yoyo: true,
      repeat: -1,
      ease: 'Linear'
    });
  }

  petCharacter() {
    if (this.isPetting) return;
    this.isPetting = true;
    sound.playPet();

    // Heart explosion
    this.heartEmitter.explode(6, this.charContainer.x, this.charContainer.y - 10);

    // Joyful squash and bounce
    this.tweens.add({
      targets: this.charContainer,
      scaleX: 1.25,
      scaleY: 0.75,
      duration: 120,
      yoyo: true,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.charContainer,
          y: '-=30',
          duration: 200,
          yoyo: true,
          ease: 'Quad.easeOut',
          onComplete: () => {
            this.isPetting = false;
          }
        });
      }
    });
  }

  destroy() {
    if (this.stateUnsubscribe) {
      this.stateUnsubscribe();
    }
  }
}
